import { Box, FormHelperText, SelectChangeEvent, Stack } from '@mui/material'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Dispatch, FunctionComponent, SetStateAction, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { IPostSubsidiary, ISubsidiary } from '../../../shared/services/subsidiary/subsidiary.model';
import { DialogSaveButtons } from '../miscellaneous-components/dialog-save-buttons';
import { useNavigate } from 'react-router-dom';
import { FormTextInputDisplay } from '../form-field-components/new-and-edit/form-text-input';
import { ICountry } from '../../../shared/services/location/location.model';
import { FormAutoCompleteInputDisplay } from '../form-field-components/new-and-edit/form-autocomplete-input';
import LocationService from '../../../shared/services/location/location.service';
import ConversionSourceService from '../../../shared/services/conversion-source/conversion-source.service';
import { IConversionSource } from '../../../shared/services/conversion-source/conversion-source.model';
import { FormSelectInputDisplay } from '../form-field-components/new-and-edit/form-select-input';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';
import { FormCheckBoxInputDisplay } from '../form-field-components/new-and-edit/form-checkbox-input';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import CustomFieldService from '../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../shared/services/custom-field/custom-field.model';
import { DEFRA_GHG_CONVERSION_SOURCE_ID, DEFRA_SPEND_CONVERSION_SOURCE_ID } from '../../../shared/constants/conversion-sources';

export const SubsidiaryForm: FunctionComponent<{
  subsidiary: ISubsidiary | IPostSubsidiary,
  isEditPage: boolean,
  onSubmit: (values: IPostSubsidiary) => void
  errorMessage?: string,
  errorCode?: number,
  isSubmitting?: boolean | undefined,
  setEditField: React.Dispatch<SetStateAction<any>>,
  editField: string,
  setErrorMessage?: Dispatch<SetStateAction<string | undefined>>,
  setErrorCode?: Dispatch<SetStateAction<number | undefined>>,
}> = ({
  subsidiary,
  errorMessage,
  errorCode,
  isEditPage = false,
  onSubmit,
  isSubmitting,
  setEditField,
  editField,
  setErrorMessage,
  setErrorCode,
}) => {
    const navigate = useNavigate();
    //Services
    const conversionSourceService = new ConversionSourceService()
    const locationService = LocationService.getInstance()
    const customFieldService = new CustomFieldService();
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    // GHG Protocol consolidation approach (org-level) decides which boundary field is relevant per subsidiary.
    const consolidationApproach = userDetailsContext.userDetails?.organisation?.consolidationApproach ?? 'OPERATIONAL_CONTROL'
    const isEquityShare = consolidationApproach === 'EQUITY_SHARE'
    const isControlApproach = consolidationApproach === 'FINANCIAL_CONTROL' || consolidationApproach === 'OPERATIONAL_CONTROL'
    //State
    const [countries, setCountries] = useState<ICountry[]>()
    const [conversionSources, setConversionSources] = useState<IConversionSource[]>()
    const [customFields, setCustomFields] = useState<ICustomField[]>();
    //Formik
    const subsidiarySchema = Yup.object().shape({
      name: Yup.string().required(`Please give this ${screenLabelsContext.getLabel('subsidiary', { plural: false })} a name`),
      countryId: Yup.string().required(`You must provide a country for your ${screenLabelsContext.getLabel('subsidiary', { plural: false })}`),
      subsidiaryReference: Yup.string().nullable(),
      defaultConversionSourceId: Yup.string().required(`You must provide a default conversion factor source for your ${screenLabelsContext.getLabel('subsidiary', { plural: false })}`),
      defaultSpendBasedConversionSourceId: Yup.string().required(`You must provide a default spend based conversion factor source for your ${screenLabelsContext.getLabel('subsidiary', { plural: false })}`),
      equitySharePercentage: Yup.number()
        .typeError('Equity share must be a number')
        .min(0).max(100).nullable()
        .transform((val, orig) => (orig === '' || orig === null ? null : val)),
      ...(customFields && customFields.length > 0 ? customFields.reduce((acc, field) => {
        if (field.isMandatory) {
          acc[field.id] = Yup.string().required(`${field.fieldName} is mandatory`);
        } else {
          acc[field.id] = Yup.string().nullable();
        }
        return acc;
      }, {} as any) : {}),
    })

    const getInitialCustomFieldValues = useCallback(() => {
      if (!customFields || !customFields.length) return {};

      return customFields.reduce((acc, field) => {
        const matchingValue = isEditPage && 'customFieldValues' in subsidiary && subsidiary.customFieldValues
          ? subsidiary.customFieldValues.find(v => v.customFieldId === field.id)?.value ?? ''
          : '';
        acc[field.id] = matchingValue;
        return acc;
      }, {} as Record<string, any>);
    }, [customFields, isEditPage, subsidiary]);

    const formik = useFormik<any>({
      initialValues: {
        name: subsidiary?.name || '',
        subsidiaryReference: subsidiary?.subsidiaryReference ? subsidiary.subsidiaryReference.split(',').join(', ') : '',
        countryId: subsidiary?.countryId || 'GBR',
        defaultConversionSourceId: subsidiary?.defaultConversionSourceId || DEFRA_GHG_CONVERSION_SOURCE_ID,
        defaultSpendBasedConversionSourceId: subsidiary?.defaultSpendBasedConversionSourceId || DEFRA_SPEND_CONVERSION_SOURCE_ID,
        isPrimary: subsidiary?.isPrimary || false,
        equitySharePercentage: subsidiary?.equitySharePercentage ?? '',
        includedInControlInventory: subsidiary?.includedInControlInventory ?? true,
        ...getInitialCustomFieldValues(),
      },
      validationSchema: subsidiarySchema,
      onSubmit: (values) => {
        const customFieldValues = Array.isArray(customFields)
          ? customFields.map((field) => ({
            customFieldId: field.id,
            value: values?.[field.id] ?? null,
          }))
          : []
        onSubmit({
          ...values,
          equitySharePercentage:
            values.equitySharePercentage === '' || values.equitySharePercentage == null
              ? null
              : Number(values.equitySharePercentage),
          customFieldValues,
        })
      }
    })

    const formikRef = useRef(formik);
    formikRef.current = formik;

    //Use Effects
    useEffect(() => {
      locationService.getAllCountries()
        .then(response => {
          setCountries(response)
        })
        .catch(err => console.log(err))

      conversionSourceService.getAll(true)
        .then(response => {
          setConversionSources(response)
        })
        .catch(err => console.log(err))

      customFieldService.getAllActive(`&entryType=subsidiary`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => {
          console.log(err)
        })
    }, [])

    useEffect(() => {
      if (customFields && customFields.length && subsidiary) {
        const customFieldValues = getInitialCustomFieldValues();

        formikRef.current.setValues({
          name: subsidiary.name,
          subsidiaryReference: subsidiary?.subsidiaryReference ? subsidiary.subsidiaryReference.split(',').join(', ') : '',
          countryId: subsidiary?.countryId,
          defaultConversionSourceId: subsidiary?.defaultConversionSourceId,
          defaultSpendBasedConversionSourceId: subsidiary?.defaultSpendBasedConversionSourceId,
          isPrimary: subsidiary?.isPrimary,
          ...customFieldValues,
        });
      }
    }, [customFields, subsidiary, isEditPage, getInitialCustomFieldValues])

    //Formik Event Handlers
    const cancelEditHandler = () => {
      setEditField('')
      formik.setTouched({})
      formik.setErrors({})
      if (setErrorMessage) {
        setErrorMessage(undefined)
      }
      if (setErrorCode) {
        setErrorCode(undefined)
      }
      if (subsidiary) {
        const originalCustomFieldValues = getInitialCustomFieldValues();

        formik.setValues({
          name: subsidiary.name,
          subsidiaryReference: subsidiary?.subsidiaryReference ? subsidiary.subsidiaryReference.split(',').join(', ') : '',
          countryId: subsidiary?.countryId,
          defaultConversionSourceId: subsidiary?.defaultConversionSourceId,
          defaultSpendBasedConversionSourceId: subsidiary?.defaultSpendBasedConversionSourceId,
          isPrimary: subsidiary?.isPrimary,
          ...originalCustomFieldValues,
        })
      }
    }

    const selectCountryHandler = (e: SelectChangeEvent<ICountry | null>, value: ICountry | null) => {
      if (value) {
        formik.setValues({ ...formik.values, countryId: value.id })
      }
    }

    const checkboxHandler = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
      const { checked } = e.target;
      formik.setValues({ ...formik.values, [name]: checked })
    }

    return (
      <Box component='form' onSubmit={formik.handleSubmit}>
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'name'}
          title='Name'
          name='name'
          value={formik.values.name}
          touched={formik.touched.name}
          errors={formik.errors.name}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          customErrorMessage={errorCode === 2 ? errorMessage : undefined}
          isTopRow={true}
        />
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'subsidiaryReference'}
          title={`${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} Reference`}
          name='subsidiaryReference'
          value={formik.values.subsidiaryReference}
          touched={formik.touched.subsidiaryReference}
          errors={formik.errors.subsidiaryReference}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          tooltip='Enter multiple references separated by commas (e.g., REF1, REF2, REF3)'
        />
        {/* TICKET-C: shown only under the Equity Share consolidation approach. */}
        {isEquityShare && (
          <FormTextInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== 'equitySharePercentage'}
            title='Equity share (%)'
            name='equitySharePercentage'
            type='number'
            value={formik.values.equitySharePercentage}
            touched={formik.touched.equitySharePercentage}
            errors={formik.errors.equitySharePercentage}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            tooltip='Your equity share of this subsidiary (GHG Protocol). Leave blank to count at 100%.'
          />
        )}
        <FormCheckBoxInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'isPrimary'}
          title={`Primary ${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}`}
          name='isPrimary'
          value={formik.values.isPrimary}
          touched={formik.touched.isPrimary}
          errors={formik.errors.isPrimary}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => checkboxHandler(e, 'isPrimary')}
          cancelEditHandler={cancelEditHandler}
          label={`Primary ${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}`}
        />
        {/* TICKET-C: shown only under Financial/Operational Control. Unchecked excludes this
            subsidiary from the control inventory (weight 0). Default checked = included (weight 1). */}
        {isControlApproach && (
          <FormCheckBoxInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== 'includedInControlInventory'}
            title='Included in control inventory'
            name='includedInControlInventory'
            value={formik.values.includedInControlInventory}
            touched={formik.touched.includedInControlInventory}
            errors={formik.errors.includedInControlInventory}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => checkboxHandler(e, 'includedInControlInventory')}
            cancelEditHandler={cancelEditHandler}
            label='Included in control inventory (financial / operational control)'
          />
        )}
        {countries &&
          <FormAutoCompleteInputDisplay
            isEditPage={isEditPage}
            allowEdit={true}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== 'countryId'}
            title='Default Country'
            name='countryId'
            value={countries?.find(country => country.id === formik.values.countryId)}
            touched={formik.touched.countryId}
            errors={formik.errors.countryId}
            onChange={(e: any, value: ICountry | null) => selectCountryHandler(e, value)}
            cancelEditHandler={cancelEditHandler}
            options={countries.slice().sort((a, b) => (a.name === 'United Kingdom' ? -1 : b.name === 'United Kingdom' ? 1 : a.name.localeCompare(b.name)))}
            optionLabelKey={'name'}
            optionImage={'imageUrl'}
            customErrorMessage={errorCode === 3 ? errorMessage : undefined}
          />
        }
        {conversionSources &&
          <>
            <FormSelectInputDisplay
              isEditPage={isEditPage}
              allowEdit={true}
              editField={editField}
              setEditField={setEditField}
              disabled={isEditPage && editField !== 'defaultConversionSourceId'}
              title='Default Conversion Factor Source'
              name='defaultConversionSourceId'
              isLastField={true}
              value={formik.values.defaultConversionSourceId}
              touched={formik.touched.defaultConversionSourceId}
              errors={formik.errors.defaultConversionSourceId}
              onChange={formik.handleChange}
              cancelEditHandler={cancelEditHandler}
              array={conversionSources.filter(source => source.conversionType === 'conversion').sort((a, b) => a.source.localeCompare(b.source))}
              valueKey={'id'}
              labelKey={'source'}
              customErrorMessage={errorCode === 4 ? errorMessage : undefined}
            />
          </>
        }
        {conversionSources &&
          <>
            <FormSelectInputDisplay
              isEditPage={isEditPage}
              allowEdit={true}
              editField={editField}
              setEditField={setEditField}
              disabled={isEditPage && editField !== 'defaultSpendBasedConversionSourceId'}
              title='Default Spend Conversion Factor Source'
              name='defaultSpendBasedConversionSourceId'
              isLastField={true}
              value={formik.values.defaultSpendBasedConversionSourceId}
              touched={formik.touched.defaultSpendBasedConversionSourceId}
              errors={formik.errors.defaultSpendBasedConversionSourceId}
              onChange={formik.handleChange}
              cancelEditHandler={cancelEditHandler}
              array={conversionSources.filter(source => source.conversionType === 'spendBasedConversion').sort((a, b) => a.source.localeCompare(b.source))}
              valueKey={'id'}
              labelKey={'source'}
              isBottomRow={customFields && customFields.length > 0 ? false : true}
              customErrorMessage={errorCode === 5 ? errorMessage : undefined}
            />
          </>
        }
        {isEditPage && <Stack sx={{ mb: 2 }}></Stack>}
        {(errorCode === 1 || errorCode === 6) &&
          <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}
          </FormHelperText>
        }
        {customFields && customFields.length > 0 && customFields.map((field, index) => (
          <FormTextInputDisplay
            key={field.id}
            isBottomRow={index === customFields.length - 1}
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== field.id}
            title={field.fieldName}
            name={field.id}
            type={field.fieldType === 'number' ? 'number' as const : 'text' as const}
            value={formik.values[field.id]}
            touched={formik.touched[field.id]}
            errors={formik.errors[field.id]}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
          />
        ))
        }
        {!isEditPage &&
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isSubmitting}
              cancelHandler={() => navigate('/organisation/subsidiaries')}
              submitHandler={formik.submitForm}
            />
          </Stack>}
      </Box>
    )
  }